import { Server } from '../../types/server'
import { ApplicationImportance } from '../../types/application-importance'
import { Connector } from '../../../../api/connector'
import { DiagnosticCollectionSettings } from '../../../../components/servers-grid/shared'

export function normalizeServer(
  {
    server_id: id,
    name,
    environment,
    applications,
    assess,
    status,
    tags,
    license,
    hostname,
    path,
    logLevel,
    first_activity: firstActivity,
    last_activity: lastActivity,
    agent_version: agentVersion,
    latest_agent_version: latestAgentVersion,
    out_of_date: outOfDate,
    language,
    syslog_enabled,
    syslog_ip_address,
    defend: protect = false,
    is_assess_status_locked: isAssessStatusLocked,
    is_protect_status_locked: isProtectStatusLocked,
    assess_enable_property_name: assessEnablePropertyName,
    protect_enable_property_name: protectEnablePropertyName,
    assess_enable_location: assessEnableLocation,
    protect_enable_location: protectEnableLocation,
    diagnostic_collection_enabled: diagnosticCollectionEnabled
  },
  {
    stacktrace_capture_mode: stacktraceCaptureMode,
    log_path: logPath,
    bot_protection: botProtection,
    sampling,
    sampling_profile: samplingProfile,
    sampling_frequency,
    sampling_baseline,
    sampling_window,
    syslog_port_number,
    syslog_facility_code,
    syslog_protocol,
    syslog_severity_exploited,
    syslog_severity_suspicious,
    syslog_severity_blocked,
    syslog_severity_blocked_perimeter,
    syslog_severity_probed
  }
): Server {
  return {
    id,
    name,
    environment,
    assess,
    applications: applications.map(
      ({
        app_id: applicationUuid,
        name,
        language,
        importance_description: importance,
        path,
        has_instrumentation_conflict: hasInstrumentationConflict
      }) => {
        return {
          /* hasInstrumentationConflict can be different for each server/app combination
          so we need the cache ID for ServerApplication to include the server ID */
          id: `${applicationUuid}|${id}`,
          applicationUuid,
          name,
          language,
          importance: importance as ApplicationImportance,
          path,
          hasInstrumentationConflict
        }
      }
    ),
    online: status === 'ONLINE',
    tags,
    license: license.licensed
      ? {
          expiration: license.end
        }
      : null,
    logLevel,
    hostname,
    path,
    firstActivity,
    lastActivity,
    agentVersion,
    latestAgentVersion,
    outOfDate,
    language,
    isAssessStatusLocked,
    isProtectStatusLocked,
    assessEnablePropertyName,
    protectEnablePropertyName,
    assessEnableLocation,
    protectEnableLocation,
    syslogConfig: {
      enabled: syslog_enabled,
      ipAddress: syslog_ip_address,
      port: syslog_port_number,
      facilityCode: syslog_facility_code,
      protocol: syslog_protocol,
      severityExploited: syslog_severity_exploited,
      severitySuspicious: syslog_severity_suspicious,
      severityBlocked: syslog_severity_blocked,
      severityBlockedPerimeter: syslog_severity_blocked_perimeter,
      severityProbed: syslog_severity_probed
    },
    protect,
    stacktraceCaptureMode,
    logPath,
    botProtection,
    samplingProfile,
    sampling: {
      enabled: sampling,
      baseline: sampling_baseline,
      frequency: sampling_frequency,
      window: sampling_window
    },
    diagnosticCollectionEnabled
  }
}

export async function findByIDAndOrganizationID(
  serverID: string,
  organizationID: string,
  connector: Connector
): Promise<Server> {
  const { server } = await connector.get(
    `/${organizationID}/servers/${serverID}?expand=applications,server_license,assess_protect_status_locked`
  )

  const { settings } = await connector.get(
    `/${organizationID}/servers/${serverID}/edit`
  )

  return normalizeServer(server, settings)
}

export async function updateDiagnosticCollection(
  serverID: string,
  organizationID: string,
  connector: Connector
): Promise<DiagnosticCollectionSettings> {
  const { diagnosticCollection } = await connector.get(
    `/organizations/${organizationID}/servers/${serverID}/diagnostic`
  )

  return normalizeDiagnostics(diagnosticCollection)
}
export function normalizeDiagnostics({
  uuid,
  enabled,
  log_level: logLevel,
  start_time: startTime,
  expires
}): DiagnosticCollectionSettings {
  return {
    uuid,
    enabled,
    logLevel,
    startTime,
    expires
  }
}
